import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '../../utils/cn';
import { Button, TEXT_BUTTON_SIZE, TEXT_BUTTON_TYPE, ButtonProps } from '../Button';
import { CheckCircleFilledIcon } from '../icons/Check';
import {
  AlertCircleFilledIcon,
  AlertTriangleFilledIcon,
  AlertHexagonFilledIcon,
} from '../icons/Alert';
import { TrashFilledIcon } from '../icons/Trash';
import { XIcon } from '../icons/X';

interface AlertDialogContextValue {
  disabled?: boolean;
}

const AlertDialogContext = React.createContext<AlertDialogContextValue>({});

const useAlertDialogContext = () => React.useContext(AlertDialogContext);

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/65 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const alertDialogSizeVariants = {
  sm: 'max-w-[402px]',
  md: 'max-w-[522px]'
} as const;

type AlertDialogSize = keyof typeof alertDialogSizeVariants;

interface AlertDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> {
  size?: AlertDialogSize;
  disabled?: boolean;
}

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, size = 'md', disabled, children, ...props }, ref) => {
  const { disabled: contextDisabled } = useAlertDialogContext();
  const isDisabled = disabled ?? contextDisabled;

  return (
    <AlertDialogContext.Provider value={{ disabled: isDisabled }}>
      <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogPrimitive.Content
          ref={ref}
          onEscapeKeyDown={(e) => isDisabled && e.preventDefault()}
          className={cn(
            'bg-white border border-secondary rounded-m',
            'fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] border shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            alertDialogSizeVariants[size],
            className
          )}
          {...props}
        >
          <AlertDialogPrimitive.Cancel asChild disabled={isDisabled}>
            <Button
              variant={TEXT_BUTTON_TYPE.TERTIARY_GRAY}
              size={TEXT_BUTTON_SIZE.S}
              iconOnly
              aria-label="Close"
              className="absolute right-l top-l"
              disabled={isDisabled}
            >
              <XIcon />
            </Button>
          </AlertDialogPrimitive.Cancel>
          {children}
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    </AlertDialogContext.Provider>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

type AlertDialogVariant = 'warning' | 'destructive' | 'success' | 'info' | 'error';

interface AlertDialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertDialogVariant;
}

const AlertDialogHeaderIcon = ({ variant }: { variant: AlertDialogVariant }) => {
  switch (variant) {
    case 'warning':
      return (
        <div className="flex items-center justify-center w-8xl h-8xl rounded-full border-[6px] border-warning-50 bg-warning-100">
          <AlertTriangleFilledIcon size="lg" className="text-warning-600" />
        </div>
      );
    case 'destructive':
      return (
        <div className="flex items-center justify-center w-8xl h-8xl rounded-full border-[6px] border-error-50 bg-error-100">
          <TrashFilledIcon className="text-badge-red" />
        </div>
      );
    case 'success':
      return (
        <div className="flex items-center justify-center w-8xl h-8xl rounded-full border-[6px] border-success-50 bg-success-100">
          <CheckCircleFilledIcon className="text-badge-green" />
        </div>
      );
    case 'error':
      return (
        <div className="flex items-center justify-center w-8xl h-8xl rounded-full border-[6px] border-error-50 bg-error-100">
          <AlertHexagonFilledIcon className="text-badge-red" />
        </div>
      );
    case 'info':
      return (
        <div className="flex items-center justify-center w-8xl h-8xl rounded-full border-[6px] border-blue-50 bg-blue-100">
          <AlertCircleFilledIcon className="text-badge-blue" />
        </div>
      );
    default: {
      return null;
    }
  }
};

const AlertDialogHeader = ({
  className,
  variant,
  children,
  ...props
}: AlertDialogHeaderProps) => (
  <div
    className={cn('gap-l px-3xl pt-[22px]', 'flex flex-col', className)}
    {...props}
  >
    {variant && <AlertDialogHeaderIcon variant={variant} />}
    {children}
  </div>
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'h-[68px] flex flex-row items-center justify-end gap-2xs px-3xl py-l border-t border-secondary',
      className
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    asChild
    className={cn('px-3xl pb-3xl pt-2xl text-sm text-secondary leading-lg', className)}
    {...props}
  >
    <div>{props.children}</div>
  </AlertDialogPrimitive.Description>
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-xl text-primary font-semibold', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogAction = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant = TEXT_BUTTON_TYPE.PRIMARY, size = TEXT_BUTTON_SIZE.S, ...props }, ref) => (
  <Button
    ref={ref}
    variant={variant}
    size={size}
    className={className}
    {...props}
  />
));
AlertDialogAction.displayName = 'AlertDialogAction';

const AlertDialogCancel = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant = TEXT_BUTTON_TYPE.SECONDARY_GRAY, size = TEXT_BUTTON_SIZE.S, disabled: disabledProp, ...props }, ref) => {
  const { disabled: contextDisabled } = useAlertDialogContext();
  const disabled = disabledProp ?? contextDisabled;

  return (
    <AlertDialogPrimitive.Cancel asChild disabled={disabled}>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={className}
        disabled={disabled}
        {...props}
      />
    </AlertDialogPrimitive.Cancel>
  );
});
AlertDialogCancel.displayName = 'AlertDialogCancel';

export {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogDescription as AlertDialogBody,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel
};
